import axios from "axios";
import validadorDeCpf from "./validadorCpf";
import validadorDeCnpj from "./validadorCnpj";

// Microserviço de validação de CPF/CNPJ (ADM). Recebe o documento (formatado
// ou apenas números) e retorna se é válido, o tipo identificado e, no caso de
// CNPJ encontrado na Receita, os dados cadastrais da empresa.
const validadorApi = axios.create({
  baseURL: process.env.REACT_APP_VALIDADOR_API_HOST, // rota de desenvolvimento/produção definida no .env
});

const TIMEOUT_MS = 5000;

const apenasNumeros = (documento = "") => documento.replace(/[^\d]+/g, "");

const formatarCpf = (cpf) =>
  cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");

const formatarCnpj = (cnpj) =>
  cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");

// Validação local (mesmas regras de validadorCpf.js/validadorCnpj.js), usada
// como contingência quando o microserviço não responde, para não travar o
// usuário no meio do processo.
const validarLocalmente = (documento) => {
  const numeros = apenasNumeros(documento);

  if (numeros.length === 11) {
    const valido = validadorDeCpf(numeros);
    return {
      valido,
      tipo: "cpf",
      documento: numeros,
      documentoFormatado: formatarCpf(numeros),
      dados: null,
      erro: valido ? null : "CPF inválido",
      fallback: true,
    };
  }

  if (numeros.length === 14) {
    const valido = validadorDeCnpj(numeros);
    return {
      valido,
      tipo: "cnpj",
      documento: numeros,
      documentoFormatado: formatarCnpj(numeros),
      dados: null,
      erro: valido ? null : "CNPJ inválido",
      fallback: true,
    };
  }

  return {
    valido: false,
    tipo: null,
    documento: numeros,
    documentoFormatado: documento,
    dados: null,
    erro: "Informe um CPF ou CNPJ válido.",
    fallback: true,
  };
};

// Consulta o microserviço de validação de documentos. O retorno segue o
// mesmo formato tanto em caso de sucesso (200) quanto de documento inválido
// (422): { valido, tipo, documento, documentoFormatado, dados, erro }.
const validadorCpfCnpj = async (documento) => {
  if (!documento) {
    return {
      valido: false,
      tipo: null,
      documento: "",
      documentoFormatado: "",
      dados: null,
      erro: "Informe um CPF ou CNPJ.",
      fallback: true,
    };
  }

  try {
    const { data } = await validadorApi.post(
      "/servicos/validar-documento",
      { documento },
      { timeout: TIMEOUT_MS }
    );
    return data;
  } catch (error) {
    // O serviço respondeu com um erro de validação (ex: 422 - documento
    // inválido): o corpo já vem no formato esperado, basta repassar.
    if (error.response?.data) {
      return error.response.data;
    }

    // Chamada de emergência: o microserviço não respondeu (timeout, rede
    // offline, etc.). Valida localmente para não travar o usuário.
    return validarLocalmente(documento);
  }
};

export default validadorCpfCnpj;
